import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth'
import { toast } from 'sonner'
import { auth, googleProvider } from './app'
import { authErrorMessage } from './auth-errors'
import { clearEmailLink, rememberedLinkEmail, sendEmailLink } from './email-link'

export type AuthStatus = 'loading' | 'signedOut' | 'signedIn'

/**
 * Progress of an email-link sign-in when the console was opened from the
 * link: `completing` while we redeem it, `needsEmail` when this browser
 * doesn't remember which address the link was sent to.
 */
export type EmailLinkStatus = 'none' | 'completing' | 'needsEmail'

interface AuthContextValue {
  status: AuthStatus
  user: User | null
  signInWithGoogle: () => Promise<void>
  /** Rejects with the raw Firebase error — render it with `authErrorMessage`. */
  signInWithEmail: (email: string, password: string) => Promise<void>
  sendPasswordReset: (email: string) => Promise<void>
  /** Emails a passwordless sign-in link that brings the person back to this page. */
  sendSignInLink: (email: string) => Promise<void>
  emailLinkStatus: EmailLinkStatus
  /** Finishes a `needsEmail` link sign-in. Rejects with the raw Firebase error. */
  completeEmailLinkSignIn: (email: string) => Promise<void>
  cancelEmailLinkSignIn: () => void
  signOutAdmin: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Mirrors voyp-webapp's AuthProvider, minus sign-up: admin accounts are
 * existing VOYP users that have been given the `admin` role, never created here.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ status: AuthStatus; user: User | null }>({
    status: 'loading',
    user: null,
  })
  const [emailLinkStatus, setEmailLinkStatus] = useState<EmailLinkStatus>(() =>
    isSignInWithEmailLink(auth, window.location.href) ? 'completing' : 'none',
  )
  // A link's code is single-use, so StrictMode's double effect must not redeem it twice.
  const emailLinkHandled = useRef(false)

  useEffect(
    () =>
      onAuthStateChanged(auth, (user) => {
        setState({ status: user ? 'signedIn' : 'signedOut', user })
      }),
    [],
  )

  const completeEmailLinkSignIn = useCallback(async (email: string) => {
    await signInWithEmailLink(auth, email.trim(), window.location.href)
    clearEmailLink()
    setEmailLinkStatus('none')
  }, [])

  const cancelEmailLinkSignIn = useCallback(() => {
    clearEmailLink({ keepEmail: true })
    setEmailLinkStatus('none')
  }, [])

  useEffect(() => {
    if (state.status === 'loading' || emailLinkStatus !== 'completing') return
    if (emailLinkHandled.current) return
    emailLinkHandled.current = true

    if (state.status === 'signedIn') {
      clearEmailLink({ keepEmail: true })
      setEmailLinkStatus('none')
      toast.info("You're already signed in")
      return
    }

    const email = rememberedLinkEmail()
    if (!email) {
      setEmailLinkStatus('needsEmail')
      return
    }
    completeEmailLinkSignIn(email).catch((error: unknown) => {
      console.error('Email link sign-in failed', error)
      toast.error('Sign-in failed', { description: authErrorMessage(error) })
      cancelEmailLinkSignIn()
    })
  }, [state.status, emailLinkStatus, completeEmailLinkSignIn, cancelEmailLinkSignIn])

  const signInWithGoogle = useCallback(async () => {
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (error) {
      const code = (error as { code?: string }).code
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return
      console.error('Sign-in failed', error)
      toast.error('Sign-in failed', { description: authErrorMessage(error) })
    }
  }, [])

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email.trim(), password)
  }, [])

  const sendPasswordReset = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim())
  }, [])

  const sendSignInLink = useCallback((email: string) => sendEmailLink(auth, email), [])

  const signOutAdmin = useCallback(async () => {
    await signOut(auth)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signInWithGoogle,
        signInWithEmail,
        sendPasswordReset,
        sendSignInLink,
        emailLinkStatus,
        completeEmailLinkSignIn,
        cancelEmailLinkSignIn,
        signOutAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
